import { useState, useEffect } from 'react';
import { useAuthStore } from '@shared/store/authStore';

// VAPID Public Key из sw.js
const VAPID_PUBLIC_KEY = 'BLpofeVrXs37QO2ndhRYtswpuPb9F7c35NnlnUQex3gL4XIwAL_Fn-Uf8qN4PBqLqL4DkiZ8UG8TuzrB2bq5Kf8';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function PushNotificationSetup() {
  const { user } = useAuthStore();
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
      checkSubscription();
    }
  }, []);

  const checkSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
    } catch (error) {
      console.error('[Push] Error checking subscription:', error);
    }
  };

  const handleEnableNotifications = async () => {
    setLoading(true);

    try {
      console.log('[Push] Requesting permission...');

      // Запрашиваем разрешение (требуется user gesture)
      const permission = await Notification.requestPermission();
      console.log('[Push] Permission result:', permission);

      setPermission(permission);

      if (permission === 'granted') {
        console.log('[Push] Permission granted, subscribing to push...');

        // Получаем Service Worker registration
        const registration = await navigator.serviceWorker.ready;
        console.log('[Push] Service Worker ready');

        // Подписываемся на push уведомления
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
        });

        console.log('[Push] Subscription created:', subscription);

        // Получаем clientId из user
        const clientId = user?.id;
        console.log('[Push] Sending subscription with clientId:', clientId);

        // Отправляем подписку на сервер с clientId
        const response = await fetch('/api/push/subscribe', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            subscription,
            clientId
          })
        });

        if (response.ok) {
          console.log('[Push] Subscription sent to server');
          setIsSubscribed(true);

          // Показываем тестовое уведомление
          registration.showNotification('Level Sport Club', {
            body: 'Уведомления успешно включены!',
            icon: '/logotype-white.png',
            badge: '/logotype-white.png'
          });
        } else {
          console.error('[Push] Failed to send subscription to server:', response.status);
        }
      }
    } catch (error) {
      console.error('[Push] Error enabling notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Если уведомления не поддерживаются
  if (!('Notification' in window)) {
    return null;
  }

  // Если разрешение уже получено и подписка активна
  if (permission === 'granted' && isSubscribed) {
    return (
      <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl p-4">
        <div className="flex items-start space-x-3">
          <span className="text-2xl">✅</span>
          <div className="flex-1">
            <h3 className="font-semibold text-emerald-900 dark:text-emerald-100">
              Уведомления включены
            </h3>
            <p className="text-sm text-emerald-700 dark:text-emerald-300 mt-1">
              Вы будете получать уведомления о записях, акциях и новостях клуба
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Если разрешение отклонено
  if (permission === 'denied') {
    return (
      <div className="bg-orange-50 dark:bg-orange-900/20 rounded-2xl p-4">
        <div className="flex items-start space-x-3">
          <span className="text-2xl">🔕</span>
          <div className="flex-1">
            <h3 className="font-semibold text-orange-900 dark:text-orange-100">
              Уведомления отключены
            </h3>
            <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
              Чтобы включить уведомления, разрешите их в настройках Safari для этого сайта
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Показываем кнопку включения
  return (
    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-2xl p-4">
      <div className="flex items-start space-x-3">
        <span className="text-2xl">🔔</span>
        <div className="flex-1">
          <h3 className="font-semibold text-blue-900 dark:text-blue-100">
            Включите уведомления
          </h3>
          <p className="text-sm text-blue-700 dark:text-blue-300 mt-1 mb-3">
            Получайте напоминания о тренировках, акциях и новостях клуба
          </p>
          <button
            onClick={handleEnableNotifications}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Подключение...' : 'Включить уведомления'}
          </button>
        </div>
      </div>
    </div>
  );
}
